import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TimeSlotModal = ({ 
  isOpen, 
  onClose, 
  timeSlot, 
  onSave 
}) => {
  const [formData, setFormData] = useState({
    status: timeSlot?.currentStatus || 'available',
    startTime: '۰۹:۰۰',
    endTime: '۰۹:۳۰',
    notes: '',
    recurring: false,
    recurringDays: []
  });

  const statusOptions = [
    { value: 'available', label: 'در دسترس' },
    { value: 'busy', label: 'رزرو شده' },
    { value: 'unavailable', label: 'غیرفعال' }
  ];

  const timeOptions = [
    { value: '۰۸:۰۰', label: '۰۸:۰۰' },
    { value: '۰۸:۳۰', label: '۰۸:۳۰' },
    { value: '۰۹:۰۰', label: '۰۹:۰۰' },
    { value: '۰۹:۳۰', label: '۰۹:۳۰' },
    { value: '۱۰:۰۰', label: '۱۰:۰۰' },
    { value: '۱۰:۳۰', label: '۱۰:۳۰' },
    { value: '۱۱:۰۰', label: '۱۱:۰۰' },
    { value: '۱۱:۳۰', label: '۱۱:۳۰' },
    { value: '۱۲:۰۰', label: '۱۲:۰۰' },
    { value: '۱۲:۳۰', label: '۱۲:۳۰' },
    { value: '۱۳:۰۰', label: '۱۳:۰۰' },
    { value: '۱۳:۳۰', label: '۱۳:۳۰' },
    { value: '۱۴:۰۰', label: '۱۴:۰۰' },
    { value: '۱۴:۳۰', label: '۱۴:۳۰' },
    { value: '۱۵:۰۰', label: '۱۵:۰۰' },
    { value: '۱۵:۳۰', label: '۱۵:۳۰' },
    { value: '۱۶:۰۰', label: '۱۶:۰۰' },
    { value: '۱۶:۳۰', label: '۱۶:۳۰' },
    { value: '۱۷:۰۰', label: '۱۷:۰۰' },
    { value: '۱۷:۳۰', label: '۱۷:۳۰' },
    { value: '۱۸:۰۰', label: '۱۸:۰۰' },
    { value: '۱۸:۳۰', label: '۱۸:۳۰' },
    { value: '۱۹:۰۰', label: '۱۹:۰۰' },
    { value: '۱۹:۳۰', label: '۱۹:۳۰' }
  ];

  const persianDays = [
    { value: 'saturday', label: 'شنبه' },
    { value: 'sunday', label: 'یکشنبه' },
    { value: 'monday', label: 'دوشنبه' },
    { value: 'tuesday', label: 'سه‌شنبه' },
    { value: 'wednesday', label: 'چهارشنبه' },
    { value: 'thursday', label: 'پنج‌شنبه' },
    { value: 'friday', label: 'جمعه' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRecurringDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      recurringDays: prev?.recurringDays?.includes(day)
        ? prev?.recurringDays?.filter(d => d !== day)
        : [...prev?.recurringDays, day]
    }));
  };

  const handleSave = () => {
    onSave?.(formData);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-success';
      case 'busy':
        return 'text-error';
      case 'unavailable':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available':
        return 'Check';
      case 'busy':
        return 'X';
      case 'unavailable':
        return 'Minus';
      default:
        return 'Clock';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-modal z-modal flex items-center justify-center p-4">
      <div className="bg-popover border border-border rounded-lg shadow-elevation-4 w-full max-w-md animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Clock" size={20} />
            تنظیم زمان‌بندی
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          >
            <span className="sr-only">بستن</span>
          </Button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current Status */}
          <div className="bg-muted rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Info" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">وضعیت فعلی</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon 
                name={getStatusIcon(timeSlot?.currentStatus)} 
                size={16} 
                className={getStatusColor(timeSlot?.currentStatus)}
              />
              <span className={`text-sm ${getStatusColor(timeSlot?.currentStatus)}`}>
                {statusOptions?.find(opt => opt?.value === timeSlot?.currentStatus)?.label || 'نامشخص'}
              </span>
            </div>
          </div>

          {/* Status Selection */}
          <Select
            label="وضعیت جدید"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleInputChange('status', value)}
            required
          />

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="زمان شروع"
              options={timeOptions}
              value={formData?.startTime}
              onChange={(value) => handleInputChange('startTime', value)}
              required
            />
            <Select
              label="زمان پایان"
              options={timeOptions}
              value={formData?.endTime}
              onChange={(value) => handleInputChange('endTime', value)}
              required
            />
          </div>

          {/* Notes */}
          <Input
            label="یادداشت (اختیاری)"
            type="text"
            placeholder="توضیحات اضافی..."
            value={formData?.notes}
            onChange={(e) => handleInputChange('notes', e?.target?.value)}
          />

          {/* Recurring Options */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="recurring"
                checked={formData?.recurring}
                onChange={(e) => handleInputChange('recurring', e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="recurring" className="text-sm font-medium text-foreground">
                تکرار در روزهای مشخص
              </label>
            </div>

            {formData?.recurring && (
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm text-muted-foreground mb-2">انتخاب روزهای تکرار:</p>
                <div className="grid grid-cols-2 gap-2">
                  {persianDays?.map((day) => (
                    <button
                      key={day?.value}
                      onClick={() => handleRecurringDayToggle(day?.value)}
                      className={`
                        p-2 text-sm rounded border transition-colors duration-150
                        ${formData?.recurringDays?.includes(day?.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-foreground border-border hover:bg-muted'
                        }
                      `}
                    >
                      {day?.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
          >
            انصراف
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            iconName="Save"
            iconPosition="right"
          >
            ذخیره تغییرات
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotModal;